import { useMemo } from 'react';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import { TabTableContainer } from 'components/layout/AppLayout/AppLayout.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import { ENUMS, PRICE_CONFIGURATIONS } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author Burak Ak
 */

const SpecialContractTermsList = () => {
	const defaultSort = useMemo(() => [{ id: 'id', desc: true }], []);

	const columns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'shippingContractName',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				showOnMobile: true,
				width: 100
			},
			{
				Header: 'deliveryCompanySettingsId',
				accessor: 'deliveryCompanySettingsId',
				filterType: FilterType.number,
				width: 100,
				show: false
			},
			{
				Header: 'agreementType',
				accessor: 'agreementType',
				filterType: FilterType.enumMulti,
				width: 100,
				enumName: ENUMS.SHIPPING_PRICE_AGREEMENT_TYPE
			},
			{
				Header: 'status',
				accessor: 'shippingContractStatus',
				filterType: FilterType.enumMulti,
				width: 100,
				enumName: ENUMS.YES_NO
			},
			{
				Header: 'clientName',
				accessor: 'clientName',
				filterType: FilterType.string,
				showOnMobile: true,
				width: 100
			},
			{
				Header: 'clientPlan',
				accessor: 'clientPlan',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_V2_SMB_CLIENT_TYPES,
				showOnMobile: true,
				width: 100
			},
			{
				Header: 'originCountry',
				accessor: 'originCountry',
				filterType: FilterType.string
			},
			{
				Header: 'originCity',
				accessor: 'originCity',
				filterType: FilterType.string
			},
			{
				Header: 'destinationCountry',
				accessor: 'destinationCountry',
				filterType: FilterType.string
			},
			{
				Header: 'destinationCity',
				accessor: 'destinationCity',
				filterType: FilterType.string
			},
			{
				Header: 'originTier',
				accessor: 'originTier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_TIER_TYPE
			},
			{
				Header: 'destinationTier',
				accessor: 'destinationTier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_TIER_TYPE
			},
			{
				Header: 'isSameCity',
				accessor: 'isSameCityAsString',
				filterType: FilterType.string,
				renderCell: ListComponents.STATUS
			},
			{
				Header: 'isBetweenCity',
				accessor: 'isBetweenCityAsString',
				filterType: FilterType.string,
				renderCell: ListComponents.STATUS
			},
			{
				Header: 'greaterThanKm',
				accessor: 'greaterThanKm',
				filterType: FilterType.float
			},
			{
				Header: 'lessThanOrEqualKm',
				accessor: 'lessThanOrEqualKm',
				filterType: FilterType.float
			},
			{
				Header: 'greaterThanWeight',
				accessor: 'greaterThanWeight',
				filterType: FilterType.float
			},
			{
				Header: 'lessThanOrEqualWeight',
				accessor: 'lessThanOrEqualWeight',
				filterType: FilterType.float
			},
			{
				Header: 'maxOrderValue',
				accessor: 'maxOrderValue',
				filterType: FilterType.float
			},
			{
				Header: 'pickupType',
				accessor: 'pickupType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_PICKUP_TYPE
			},
			{
				Header: 'deliveryType',
				accessor: 'deliveryType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_DELIVERY_TYPE
			},
			{
				Header: 'deliveryPromise',
				accessor: 'deliveryPromise',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DELIVERY_PROMISE
			},
			{
				Header: 'cutOffTime',
				accessor: 'cutOffTime',
				filterType: FilterType.string
			},
			{
				Header: 'insuranceAmount',
				accessor: 'insuranceFee',
				filterType: FilterType.float
			},
			{
				Header: 'notice',
				accessor: 'notice',
				filterType: FilterType.string
			},
			{
				Header: 'maxFreeWeight',
				accessor: 'maxFreeWeight',
				filterType: FilterType.float
			},
			{
				Header: 'extraWeightPerKg',
				accessor: 'extraWeightPerKg',
				filterType: FilterType.float,
				width: 100,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'maxFreeKm',
				accessor: 'maxFreeKm',
				filterType: FilterType.float
			},
			{
				Header: 'extraChargePerKm',
				accessor: 'extraChargePerKm',
				filterType: FilterType.float
			},
			{
				Header: 'applyCodRateAfter',
				accessor: 'applyCodRateAfter',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'codPercentage',
				accessor: 'codPercentage',
				filterType: FilterType.float
			},
			{
				Header: 'codCharge',
				accessor: 'codCharge',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'discountPercentage',
				accessor: 'discountPercentage',
				filterType: FilterType.float
			},
			{
				Header: 'deliveryPrice',
				accessor: 'basePrice',
				showOnMobile: true,
				filterType: FilterType.float
			},
			{
				Header: 'firstPriceRate',
				accessor: 'firstPriceRate',
				showOnMobile: true,
				filterType: FilterType.float
			},
			{
				Header: 'createdDate',
				accessor: 'createdDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'createdBy',
				accessor: 'createdBy',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'modifiedDate',
				accessor: 'modifiedDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'modifiedBy',
				accessor: 'modifiedBy',
				filterType: FilterType.string,
				width: 100
			}
		],
		[]
	);

	return (
		<TabTableContainer>
			<DynamicList
				id={DYNAMICLISTS.SPECIAL_CONTRACT_TERMS}
				columns={columns}
				fetchUrl={PRICE_CONFIGURATIONS.GET_SPECIAL_CONTRACT_TERM_LIST}
				defaultSort={defaultSort}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="specialContractTerms"
			/>
		</TabTableContainer>
	);
};

export default SpecialContractTermsList;
